import React from 'react';
import { Button, Form, Table } from 'react-bootstrap';
import { FaTrash } from 'react-icons/fa';
import { IFeed_Input_Link } from './types';

interface FeedLinksFormProps {
    links: IFeed_Input_Link[];
    onChange: (links: IFeed_Input_Link[]) => void;
    onAddLink: () => void;
}

const FeedLinksForm: React.FC<FeedLinksFormProps> = ({ links, onChange, onAddLink }) => {
    const handleLinkChange = (index: number, field: keyof IFeed_Input_Link, value: string) => {
        const updatedLinks = links.map((link, i) =>
            i === index ? { ...link, [field]: value } : link
        );
        onChange(updatedLinks);
    };

    const handleFileChange = (index: number, file: File | null) => {
        const updatedLinks = links.map((link, i) =>
            i === index ? { ...link, file: file ? file : '' } : link
        );
        onChange(updatedLinks);
    };

    const handleDataSourceTypeChange = (index: number, dataSourceType: 'url' | 'file') => {
        const updatedLinks = links.map((link, i) => {
            if (i === index) {
                return {
                    ...link,
                    dataSourceType,
                    link: dataSourceType === 'url' ? link.link || '' : '',
                    file: dataSourceType === 'file' ? link.file : '',
                };
            }
            return link;
        });
        onChange(updatedLinks);
    };

    const handleFileTypeChange = (index: number, fileType: 'csv' | 'xml' | 'json') => {
        const updatedLinks = links.map((link, i) =>
            i === index ? { ...link, kind: fileType, position: '' } : link
        );
        onChange(updatedLinks);
    };

    const handlePositionChange = (index: number, position: string) => {
        const updatedLinks = links.map((link, i) =>
            i === index ? { ...link, position } : link
        );
        onChange(updatedLinks);
    };

    const handleRemoveLink = (index: number) => {
        if (links.length > 1) {
            const updatedLinks = links.filter((_, i) => i !== index);
            onChange(updatedLinks);
        }
    };

    const handleRemoveFile = (index: number) => {
        const updatedLinks = links.map((link, i) =>
            i === index ? { ...link, file: '' } : link
        );
        onChange(updatedLinks);
    };

    const handleExportTypeChange = (index: number, exportType: 'csv' | 'xml' | 'xmlRss' | 'json') => {
        const updatedLinks = links.map((link, i) =>
            i === index ? { ...link, exportAs: exportType } : link
        );
        onChange(updatedLinks);
    };

    return (
        <div>
            <Form.Label>Data Sources</Form.Label>
            <Table size="sm" bordered>
                <thead>
                    <tr>
                        <th>Link Name</th>
                        <th>Type</th>
                        <th>Data Source</th>
                        <th>Data Location</th>
                        <th>Export As</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {links.map((link, index) => (
                        <tr key={index}>
                            <td>
                                <Form.Control
                                    type="text"
                                    placeholder="Link Name"
                                    value={link.name || ''}
                                    onChange={(e) => handleLinkChange(index, 'name', e.target.value)}
                                    required
                                    size="sm"
                                />
                            </td>
                            <td>
                                <Form.Control
                                    as="select"
                                    value={link.dataSourceType || 'url'}
                                    onChange={(e) => handleDataSourceTypeChange(index, e.target.value as 'url' | 'file')}
                                    size="sm"
                                >
                                    <option value="url">URL</option>
                                    <option value="file">File</option>
                                </Form.Control>
                            </td>
                            <td>
                                <div className="d-flex">
                                    {(!link.dataSourceType || link.dataSourceType === 'url') ? (
                                        <Form.Control
                                            type="url"
                                            placeholder="Link URL"
                                            value={link.link || ''}
                                            onChange={(e) => handleLinkChange(index, 'link', e.target.value)}
                                            required
                                            size="sm"
                                        />
                                    ) : (
                                        <Form.Group className='w-100'>
                                            <Form.Control
                                                type="file"
                                                required={typeof link.file === 'string' && !!link.file}
                                                disabled={typeof link.file === 'string' && !!link.file}
                                                onChange={(e) => {
                                                    const file = (e.target as HTMLInputElement)?.files?.[0] || null;
                                                    handleFileChange(index, file);
                                                }}
                                                size="sm"
                                                hidden={typeof link.file === 'string' && !!link.file}
                                            />
                                            {typeof link.file === 'string' && link.file && (
                                                <div className='w-100'>
                                                    <Form.Text className="text-muted">
                                                        {link.file}
                                                    </Form.Text>
                                                    <Button 
                                                        variant="link" 
                                                        size="sm" 
                                                        onClick={() => handleRemoveFile(index)}
                                                    >
                                                        Remove file
                                                    </Button>
                                                </div>
                                            )}
                                        </Form.Group>
                                    )}
                                    <Form.Control
                                        as="select"
                                        value={link.kind || 'csv'}
                                        onChange={(e) => handleFileTypeChange(index, e.target.value as 'csv' | 'xml' | 'json')}
                                        size="sm"
                                        className="text-center"
                                        style={{width:'80px',maxWidth:'80px'}}
                                    >
                                        <option value="csv">CSV</option>
                                        <option value="xml">XML</option>
                                        <option value="json">JSON</option>
                                    </Form.Control>
                                </div>
                            </td>
                            <td>
                                {link.kind === 'xml' || link.kind === 'json' ? (
                                    <Form.Control
                                        type="text"
                                        placeholder="Location (e.g., data.list)"
                                        value={link.position || ''}
                                        onChange={(e) => handlePositionChange(index, e.target.value)}
                                        size="sm"
                                    />
                                ) : (
                                    <Form.Control disabled value="" size="sm" />
                                )}
                            </td>
                            <td>
                                <Form.Control
                                    as="select"
                                    value={link.exportAs || 'csv'}
                                    onChange={(e) => handleExportTypeChange(index, e.target.value as 'csv' | 'xml' | 'json' | 'xmlRss')}
                                    size="sm"
                                >
                                    <option value="csv">CSV</option>
                                    <option value="xml">XML</option>
                                    <option value="xmlRss">XML (RSS)</option>
                                    <option value="json">JSON</option>
                                </Form.Control>
                            </td>
                            <td className="text-center">
                                {links.length > 1 && (
                                    <FaTrash
                                        onClick={() => handleRemoveLink(index)}
                                        style={{ color: 'red', cursor: 'pointer' }}
                                        className="react-icons"
                                    />
                                )}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </Table>
            <Button variant="primary" onClick={onAddLink} className="btn-sm">
                Add Source
            </Button>
        </div>
    );
};

export default FeedLinksForm;
